import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export const HeroSection = () => {
  const { t } = useTranslation();

  const trustBadges = [
    { icon: 'ri-earth-line', key: 'trust_worldwide' },
    { icon: 'ri-heart-line', key: 'trust_quality' },
    { icon: 'ri-shield-check-line', key: 'trust_guarantee' },
    { icon: 'ri-lock-line', key: 'trust_payment' },
  ];

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
      {/* Cosmic Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/901a80218dad2c56500cb37e832ac0c4.jpg"
          alt="Cosmic background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050010]/60 via-[#050010]/40 to-[#050010]" />
      </div>

      {/* Energy Orbs */}
      <div className="absolute top-1/3 left-1/3 w-96 h-96 rounded-full bg-violet-600/15 blur-3xl animate-pulse-slow" />
      <div className="absolute top-1/4 right-1/3 w-80 h-80 rounded-full bg-fuchsia-600/15 blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />
      <div className="absolute bottom-1/3 left-1/2 w-72 h-72 rounded-full bg-pink-600/10 blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />

      {/* Content */}
      <div className="relative z-10 w-full flex flex-col items-center text-center px-6 pt-28 pb-0">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-violet-500/30 backdrop-blur-sm mb-8">
          <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" />
          <span className="text-violet-300 text-xs font-medium tracking-widest uppercase">{t('hero_badge')}</span>
        </div>

        {/* Headline */}
        <h1 className="text-6xl lg:text-8xl font-black leading-none mb-6 max-w-5xl">
          <span className="text-white">{t('hero_title1')}</span>
          <br />
          <span className="text-transparent bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 bg-clip-text">
            {t('hero_title2')}
          </span>
        </h1>

        {/* Sjamanisme definitie banner */}
        <div className="w-full max-w-2xl mx-auto mb-7 px-6 py-4 rounded-2xl bg-white/4 border border-violet-400/20 backdrop-blur-sm">
          <p className="text-white/40 text-xs uppercase tracking-widest mb-1.5 font-semibold">{t('hero_shamBadge')}</p>
          <p className="text-white/75 text-sm leading-relaxed">
            <strong className="text-violet-300">{t('hero_shamLabel')}</strong>{' '}
            {t('hero_shamDesc')}
          </p>
        </div>

        <p className="text-white/55 text-base lg:text-lg max-w-xl leading-relaxed mb-10">
          {t('hero_subtitle')}
        </p>

        {/* CTA Buttons */}
        <div className="flex items-center gap-4 mb-14">
          <Link
            to="/shop"
            className="flex items-center gap-2 px-8 py-3.5 bg-[#0a0018] border border-violet-500/40 text-white font-semibold rounded-full text-sm whitespace-nowrap hover:bg-violet-900/30 transition-all cursor-pointer"
          >
            {t('hero_shopNow')}
          </Link>
          <Link
            to="/shop"
            className="flex items-center gap-2 px-8 py-3.5 border border-white/20 text-white font-medium rounded-full text-sm whitespace-nowrap hover:bg-white/5 transition-all cursor-pointer backdrop-blur-sm"
          >
            {t('hero_discoverCollection')}
          </Link>
        </div>

        {/* Floating Crystal Hero Image */}
        <div className="relative w-full max-w-lg h-64 mb-0 flex items-center justify-center">
          <div className="absolute w-64 h-64 rounded-full border border-violet-500/20 animate-spin-slow" />
          <div className="absolute w-80 h-80 rounded-full border border-fuchsia-500/10 animate-spin-slow" style={{ animationDirection: 'reverse', animationDuration: '15s' }} />
          <img
            src="https://readdy.ai/api/search-image?query=glowing%20magical%20amethyst%20crystal%20point%20floating%20levitating%20purple%20violet%20energy%20light%20aura%20spiritual%20power%20transparent%20background%20dramatic%20glow%20divine%20light%20cosmic%20energy%20beam&width=400&height=400&seq=crystalHero1&orientation=squarish"
            alt="Spiritual crystal"
            className="relative z-10 w-48 h-48 object-contain animate-float drop-shadow-2xl"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-violet-600/5 to-[#050010]/80 rounded-full" />
        </div>
      </div>

      {/* Trust Badges */}
      <div className="relative z-10 w-full border-t border-violet-500/10 bg-[#050010]/70 backdrop-blur-sm">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12">
            {trustBadges.map(badge => (
              <div key={badge.key} className="flex items-center gap-2.5">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${badge.icon} text-violet-400 text-base`} />
                </div>
                <span className="text-white/50 text-xs font-medium">{t(badge.key)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
